import type { Dispatch, FC, SetStateAction } from 'react';
import type {
  IChartCard,
  IChartCardDefaultItem,
  ICommonChartCard,
  IOnlyMyBrandsMap,
} from './types';

import { CurrentPageEnum, type IFilters } from '@/components/filters';
import { marketplacesWithAds } from '@/data/marketplaces';
import { useStateWithQuery } from '@/hooks';
import colorGenerator from '@/libs/color-generator';
import type { Marketplace } from '@/store/organization/projectSlice';
import type { RootState } from '@/store/store';
import { useWindowSize } from '@uidotdev/usehooks';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Filters } from './components';
import ChartWrapper from './components/chart-wrapper';
import {
  fetchAndSetDataAllTime,
  fetchAndSetDataProgress,
} from './helper-functions/set-data';
import { skeletonCardsAllTime, skeletonCardsProgress } from './skeleton-data';

export const DashboardSearchContext = createContext<{
  colorMap: IColorMap;
  setColorMap?: Dispatch<SetStateAction<IColorMap>>;
  activeBrands: string[];
  setActiveBrands?: Dispatch<SetStateAction<string[]>>;
  isLoading: boolean;
  isMyBrands: boolean;
  setIsMyBrands: Dispatch<SetStateAction<boolean>>;
  selectedProject?: string | number;
  setSelectedProject?: Dispatch<SetStateAction<string | number | undefined>>;
  onlyMyBrandsMap: IOnlyMyBrandsMap | null;
}>({
  colorMap: new Map(),
  activeBrands: [],
  isLoading: false,
  isMyBrands: false,
  setIsMyBrands: () => null,
  onlyMyBrandsMap: null,
});

export type IColorMap = Map<string, string>;

export const DashboardSearch: FC = () => {
  const { width } = useWindowSize();
  const [searchParams] = useSearchParams();

  const [cardsAllTime, setCardsAllTime] =
    useState<ICommonChartCard>(skeletonCardsAllTime);

  const [cardsProgress, setCardsProgress] = useState<ICommonChartCard>(
    skeletonCardsProgress
  );

  const [colorMap, setColorMap] = useState<IColorMap>(new Map());
  const [activeBrands, setActiveBrands] = useState<string[]>([]);

  const [isLoadingAllTime, setIsLoadingAllTime] = useState<boolean>(true);
  const [isLoadingProgress, setIsLoadingProgress] = useState<boolean>(true);
  const [filters, setFilters] = useState<null | IFilters>(null);

  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout>();
  const [isFirstLoad, setIsFirstLoad] = useState(false);

  const [isMyBrands, setIsMyBrands] = useStateWithQuery(
    JSON.parse(searchParams.get('isMyBrands') as string) ?? false,
    'isMyBrands'
  );
  const [selectedProject, setSelectedProject] = useState<
    string | number | undefined
  >(+JSON.parse(searchParams.get('selectedProject') as string) || undefined);

  const brandGroups = useSelector(
    (state: RootState) => state.brandGroups.brandGroups
  );

  const onlyMyBrandsMap = useMemo(() => {
    if (!isMyBrands) return null;

    const currentBrandGroups = brandGroups.filter(
      ({ project }) => project === selectedProject
    );

    return currentBrandGroups.reduce((acc, value) => {
      const newMap = value.brands.reduce(
        (brandAcc, { typings }) => ({ ...brandAcc, [typings]: value.color }),
        {}
      );

      return { ...acc, ...newMap };
    }, {});
  }, [brandGroups, selectedProject, isMyBrands]);

  useEffect(() => {
    clearTimeout(debounceTimer);
    const abortControllers = [...new Array(5)].map(() => new AbortController());
    setIsLoadingAllTime(true);
    setIsLoadingProgress(true);
    if (filters) {
      colorGenerator.reset();
      setColorMap(new Map());
      setActiveBrands([]);

      const setData = () => {
        fetchAndSetDataAllTime(cardsAllTime, filters, abortControllers)
          .then(({ cards, uniqueBrands }) => {
            setIsLoadingAllTime(false);
            const newColorMap = new Map();
            if (uniqueBrands?.length) {
              for (const uniqueBrand of uniqueBrands) {
                const color = colorGenerator.getColor();
                if (!newColorMap.get(uniqueBrand)) {
                  newColorMap.set(uniqueBrand, color);
                }
              }
            }
            setColorMap(newColorMap);
            setActiveBrands([...newColorMap.keys()].slice(0, 15));
            setCardsAllTime(cards);
            return uniqueBrands;
          })
          .catch((e) => {
            if (e?.name !== 'CanceledError') setIsLoadingAllTime(false);
          });
        fetchAndSetDataProgress(cardsProgress, filters, abortControllers)
          .then(({ cards }) => {
            setCardsProgress(cards);
            setIsLoadingProgress(false);
          })
          .catch((e) => {
            if (e?.name !== 'CanceledError') setIsLoadingProgress(false);
          });
      };

      if (isFirstLoad) {
        setData();
        setIsFirstLoad(false);
      } else {
        setDebounceTimer(
          setTimeout(() => {
            setData();
          }, 1500)
        );
      }
    } else {
      setCardsAllTime(skeletonCardsAllTime);
      setCardsProgress(skeletonCardsProgress);
      setIsLoadingAllTime(false);
      setIsLoadingProgress(false);
    }

    return () => {
      setIsLoadingAllTime(false);
      setIsLoadingProgress(false);
      abortControllers.forEach((abortController) => abortController.abort());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const checkShowChart = (card: IChartCardDefaultItem | IChartCard) => {
    if (!card.isOnlyBrandshelf && filters?.type === 'brandshelf') {
      return false;
    }
    if (
      card.isOnlyAds &&
      !marketplacesWithAds.includes(filters?.platform as Marketplace)
    ) {
      return false;
    }
    return true;
  };

  console.log('cardsAllTime:', cardsAllTime);

  return (
    <DashboardSearchContext.Provider
      value={{
        colorMap: colorMap,
        setColorMap,
        activeBrands,
        setActiveBrands,
        isLoading: isLoadingAllTime,
        isMyBrands,
        setIsMyBrands,
        selectedProject,
        setSelectedProject,
        onlyMyBrandsMap,
      }}
    >
      <div className="px-4 pt-6">
        <Filters
          setFilters={setFilters}
          showBrands={true}
          currentPage={CurrentPageEnum.Dashboard}
          isMyBrands={isMyBrands}
          setIsMyBrands={setIsMyBrands}
          setSelectedProject={setSelectedProject}
        />
        {/* <div className="mt-3 flex flex-wrap justify-around gap-4 font-bold">
        {[1, 2, 3].map((id) => (
          <InfoBlock key={id} />
        ))}
      </div> */}
        <div
          className={`mt-3 grid gap-3 ${
            (width as number) > 1200 && filters?.type !== 'brandshelf'
              ? 'grid-cols-2'
              : 'grid-cols-1'
          }`}
        >
          {cardsAllTime.map(
            (card, index) =>
              checkShowChart(card) && (
                <ChartWrapper
                  key={index}
                  isLoading={isLoadingAllTime}
                  card={card}
                  isNoFilters={!filters}
                  activeBrands={activeBrands}
                />
              )
          )}
          {cardsProgress.map(
            (card, index) =>
              checkShowChart(card) && (
                <ChartWrapper
                  key={index}
                  isLoading={isLoadingProgress}
                  card={card}
                  isNoFilters={!filters}
                  activeBrands={activeBrands}
                />
              )
          )}
        </div>
      </div>
    </DashboardSearchContext.Provider>
  );
};
